import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Download,
  Copy,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Eye,
  EyeOff,
  FileJson,
  FileSpreadsheet,
  Image,
  Calendar,
  Zap,
  Hash,
  ChevronDown,
  ChevronRight,
  Maximize2,
  Minimize2,
  User,
} from "lucide-react";
import { sipGetWorkOrder, sipGetResultByBarcode, sipGetSignatures } from "../api";
import "./SIPWorkOrderDetail.css";

const TRTYPE_CONFIG = {
  SIP: { name: "SIP Registration", color: "#3b82f6", bgColor: "#dbeafe" },
  CANCELLATION: { name: "Cancellation of SIP", color: "#ef4444", bgColor: "#fee2e2" },
  PAUSE: { name: "Pause of SIP", color: "#f59e0b", bgColor: "#fef3c7" },
  MODIFICATION: { name: "Modification of SIP", color: "#8b5cf6", bgColor: "#ede9fe" },
  COB: { name: "Change of Bank", color: "#10b981", bgColor: "#d1fae5" },
  COBC: { name: "Change of Bank (COBC)", color: "#14b8a6", bgColor: "#ccfbf1" },
  WSIP: { name: "Wealth SIP", color: "#0ea5e9", bgColor: "#e0f2fe" },
  OTM_CANCEL: { name: "OTM Cancellation", color: "#f97316", bgColor: "#ffedd5" },
};

const CollapsibleSection = ({ title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="sip-collapsible-section">
      <button className="sip-section-header" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        <span>{title.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</span>
      </button>
      {isOpen && <div className="sip-section-content">{children}</div>}
    </div>
  );
};

const JsonRenderer = ({ data, depth = 0 }) => {
  if (data === null || data === undefined) return <span className="sip-json-null">null</span>;
  if (typeof data === "boolean") return <span className="sip-json-boolean">{data.toString()}</span>;
  if (typeof data === "number") return <span className="sip-json-number">{data}</span>;
  if (typeof data === "string") return <span className="sip-json-string">"{data}"</span>;

  if (Array.isArray(data)) {
    if (data.length === 0) return <span className="sip-json-empty">[]</span>;
    return (
      <div className="sip-json-array">
        {data.map((item, i) => (
          <div key={i} className="sip-json-array-item" style={{ marginLeft: depth * 16 }}>
            <span className="sip-json-index">[{i}]</span>
            <JsonRenderer data={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof data === "object") {
    const entries = Object.entries(data);
    if (entries.length === 0) return <span className="sip-json-empty">{"{}"}</span>;
    return (
      <div className="sip-json-object">
        {entries.map(([key, value]) => (
          <div key={key} className="sip-json-property" style={{ marginLeft: depth * 16 }}>
            <span className="sip-json-key">{key}:</span>
            <JsonRenderer data={value} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  return <span>{String(data)}</span>;
};

const SIPWorkOrderDetail = () => {
  const { workOrderId } = useParams();
  const navigate = useNavigate();

  const [workOrder, setWorkOrder] = useState(null);
  const [extractionResult, setExtractionResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("extracted");
  const [copiedField, setCopiedField] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(true);
  const [pdfFullscreen, setPdfFullscreen] = useState(false);
  const [fileLoadError, setFileLoadError] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileBlob, setFileBlob] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [dbSignatures, setDbSignatures] = useState([]);
  const [signaturesLoading, setSignaturesLoading] = useState(false);

  useEffect(() => { fetchWorkOrder(); }, [workOrderId]);

  useEffect(() => {
    if (workOrder && workOrderId) fetchFileBlob();
  }, [workOrder, workOrderId]);

  // Fetch signatures when we have extraction result with acno
  useEffect(() => {
    const acno = extractionResult?.sip_data?.SO_ACNO || extractionResult?.otm_data?.OO_ACNO || workOrder?.acno;
    if (acno && workOrder?.fund) {
      fetchSignatures(acno, workOrder.fund);
    }
  }, [extractionResult, workOrder]);

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await sipGetWorkOrder(workOrderId);
      setWorkOrder(data);

      if (data?.barcode) {
        try {
          const result = await sipGetResultByBarcode(data.barcode, data.fund);
          setExtractionResult(result);
        } catch { /* no result yet */ }
      }
    } catch (err) {
      setError(err.message || "Failed to load work order");
    } finally {
      setLoading(false);
    }
  };

  const fetchSignatures = async (acno, fund) => {
    try {
      setSignaturesLoading(true);
      const data = await sipGetSignatures(acno, fund);
      if (data?.signatures) {
        setDbSignatures(data.signatures);
      }
    } catch (err) {
      console.log("No signatures found:", err);
    } finally {
      setSignaturesLoading(false);
    }
  };

  const fetchFileBlob = async () => {
    try {
      setFileLoading(true);
      setFileLoadError(false);
      const token = localStorage.getItem("token");
      const res = await fetch(`/sip/api/work-orders/${workOrderId}/file`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        if (blob.size > 0) {
          setFileUrl(URL.createObjectURL(blob));
          setFileBlob(blob);
        } else {
          setFileLoadError(true);
        }
      } else {
        setFileLoadError(true);
      }
    } catch {
      setFileLoadError(true);
    } finally {
      setFileLoading(false);
    }
  };

  const handleDownloadJson = () => {
    const payload = extractionResult || workOrder;
    if (!payload) return;
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${workOrder?.barcode || workOrderId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadCSV = () => {
    const payload = extractionResult || workOrder;
    if (!payload) return;
    const flat = {};
    const flatten = (obj, prefix = "") => {
      for (const [k, v] of Object.entries(obj)) {
        const key = prefix ? `${prefix}.${k}` : k;
        if (v && typeof v === "object" && !Array.isArray(v)) flatten(v, key);
        else flat[key] = v;
      }
    };
    flatten(payload);
    const headers = Object.keys(flat);
    const values = headers.map((h) => `"${String(flat[h] ?? "").replace(/"/g, '""')}"`);
    const csv = [headers.join(","), values.join(",")].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${workOrder?.barcode || workOrderId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadFile = async () => {
    if (fileBlob) {
      const url = URL.createObjectURL(fileBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = workOrder?.filename || `document-${workOrderId}`;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/sip/api/work-orders/${workOrderId}/file`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = workOrder?.filename || `document-${workOrderId}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download file: " + err.message);
    }
  };

  const copyToClipboard = useCallback((text, fieldName) => {
    navigator.clipboard.writeText(String(text)).then(() => {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    });
  }, []);

  const getStatusIcon = (wo) => {
    if (wo?.success === true) return <CheckCircle size={20} className="sip-status-icon success" />;
    if (wo?.success === false) return <AlertCircle size={20} className="sip-status-icon failed" />;
    return <Clock size={20} className="sip-status-icon pending" />;
  };

  const getStatusBadgeClass = (wo) => {
    if (wo?.success === true) return "success";
    if (wo?.success === false) return "failed";
    return "pending";
  };

  const getStatusText = (wo) => {
    if (wo?.success === true) return "Success";
    if (wo?.success === false) return "Failed";
    return "Pending";
  };

  const getTrtypeConfig = (wo) => {
    const key = (wo?.transaction_type || wo?.trtype || "").toUpperCase().split(" ")[0];
    return TRTYPE_CONFIG[key] || { name: wo?.transaction_type || wo?.trtype || "SIP Form", color: "#6b7280", bgColor: "#f3f4f6" };
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString() : "-";

  const isPdf = workOrder?.filename?.toLowerCase().endsWith(".pdf");

  const buildTableData = () => {
    const sections = {};
    sections["work_order_info"] = {
      execution_id: workOrder?.execution_id,
      barcode: workOrder?.barcode,
      fund: workOrder?.fund,
      fund_name: workOrder?.fund_name,
      transaction_type: workOrder?.transaction_type || workOrder?.trtype,
      status: workOrder?.success ? "Success" : "Failed",
      sip_status: workOrder?.sip_status,
      page_count: workOrder?.page_count,
      time_taken: workOrder?.time_taken_in_min,
      start_time: workOrder?.start_time,
      end_time: workOrder?.end_time,
    };

    if (extractionResult?.sip_data) {
      const sip = { ...extractionResult.sip_data };
      delete sip._id;
      sections["sip_extracted_data"] = sip;
    }

    if (extractionResult?.otm_data) {
      const otm = { ...extractionResult.otm_data };
      delete otm._id;
      sections["otm_extracted_data"] = otm;
    }

    if (workOrder?.error) {
      sections["error_details"] = { error: workOrder.error, errorstack: workOrder.errorstack };
    }

    return sections;
  };

  const getSignatures = () => {
    const sigs = {};
    const sipData = extractionResult?.sip_data || {};
    const otmData = extractionResult?.otm_data || {};
    const signatureKeys = ["SO_SIGN1", "SO_SIGN2", "SO_SIGN3", "SO_SIGN1_IMAGE", "SO_SIGN2_IMAGE", "SO_SIGN3_IMAGE", "signature_image", "signature", "sign_image", "OO_SIGN1", "OO_SIGN2"];

    [...Object.entries(sipData), ...Object.entries(otmData)].forEach(([key, val]) => {
      const upperKey = key.toUpperCase();
      if (signatureKeys.some((sk) => upperKey.includes(sk.toUpperCase())) && val && typeof val === "string" && (val.startsWith("data:image") || val.length > 100)) {
        sigs[key] = val;
      }
    });

    if (workOrder?.signature_images && typeof workOrder.signature_images === "object") {
      Object.assign(sigs, workOrder.signature_images);
    }

    // Add signatures from database (signature_image collection)
    if (dbSignatures.length > 0) {
      dbSignatures.forEach((sigRecord, idx) => {
        if (sigRecord.sign_1) {
          sigs[`Signature 1 (ACNO: ${sigRecord.acno})`] = sigRecord.sign_1;
        }
        if (sigRecord.sign_2) {
          sigs[`Signature 2 (ACNO: ${sigRecord.acno})`] = sigRecord.sign_2;
        }
        if (sigRecord.sign_3) {
          sigs[`Signature 3 (ACNO: ${sigRecord.acno})`] = sigRecord.sign_3;
        }
      });
    }

    return sigs;
  };

  if (loading) {
    return (
      <div className="sip-detail-loading">
        <RefreshCw size={32} className="sip-spin" />
        <p>Loading work order...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sip-detail-error">
        <AlertCircle size={48} />
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate("/sip/history")}>
          <ArrowLeft size={18} /> Back to Work Orders
        </button>
      </div>
    );
  }

  if (!workOrder) return null;

  const trtypeConfig = getTrtypeConfig(workOrder);
  const tableData = buildTableData();
  const signatures = getSignatures();
  const statusClass = getStatusBadgeClass(workOrder);

  return (
    <div className="sip-work-order-detail">
      <button className="sip-back-btn" onClick={() => navigate("/sip/history")}>
        <ArrowLeft size={18} /> Back to Work Orders
      </button>

      <div className="sip-detail-header" style={{ borderLeftColor: trtypeConfig.color }}>
        <div className="sip-detail-header-left">
          <div className="sip-detail-title">
            <h1><FileText size={24} />{workOrder.barcode || workOrder.filename || "Work Order"}</h1>
            <span className="sip-form-badge" style={{ backgroundColor: trtypeConfig.bgColor, color: trtypeConfig.color }}>
              {trtypeConfig.name}
            </span>
          </div>
          <div className="sip-detail-meta">
            <span className="sip-meta-item">
              <Hash size={14} /><strong>Execution ID:</strong> {workOrder.execution_id}
              <button className="sip-copy-btn-inline" onClick={() => copyToClipboard(workOrder.execution_id, "exec_id")} title="Copy">
                <Copy size={14} />
                {copiedField === "exec_id" && <span className="sip-copied-tooltip">Copied!</span>}
              </button>
            </span>
            {workOrder.fund && (
              <span className="sip-meta-item">
                <User size={14} /><strong>Fund:</strong> {workOrder.fund}{workOrder.fund_name ? ` – ${workOrder.fund_name}` : ""}
              </span>
            )}
          </div>
        </div>
        <div className="sip-detail-header-right">
          <div className={`sip-detail-status-badge sip-status-${statusClass}`}>
            {getStatusIcon(workOrder)}
            {getStatusText(workOrder)}
          </div>
        </div>
      </div>

      <div className="sip-info-cards">
        <div className="sip-info-card"><Calendar size={20} /><div><span className="label">Created</span><span className="value">{formatDate(workOrder.created_at)}</span></div></div>
        <div className="sip-info-card"><Clock size={20} /><div><span className="label">Processing Time</span><span className="value">{workOrder.time_taken_in_min ? `${workOrder.time_taken_in_min} min` : "-"}</span></div></div>
        <div className="sip-info-card"><FileText size={20} /><div><span className="label">Pages</span><span className="value">{workOrder.page_count || "-"}</span></div></div>
        <div className="sip-info-card"><Zap size={20} /><div><span className="label">SIP Status</span><span className="value" style={{ fontSize: 13 }}>{workOrder.sip_status || "-"}</span></div></div>
        <div className="sip-info-card"><Hash size={20} /><div><span className="label">Barcode</span><span className="value" style={{ fontSize: 13 }}>{workOrder.barcode || "-"}</span></div></div>
      </div>

      <div className="sip-action-bar">
        <button className={`sip-action-btn toggle ${showPdfViewer ? "active" : ""}`} onClick={() => setShowPdfViewer(!showPdfViewer)}>
          {showPdfViewer ? <EyeOff size={16} /> : <Eye size={16} />}
          <span>{showPdfViewer ? "Hide Doc" : "Show Doc"}</span>
        </button>
        <div className="sip-action-divider" />
        <button className="sip-action-btn primary" onClick={handleDownloadJson}>
          <FileJson size={16} />
          <span>JSON</span>
        </button>
        <button className="sip-action-btn secondary" onClick={handleDownloadCSV}>
          <FileSpreadsheet size={16} />
          <span>CSV</span>
        </button>
        <button className="sip-action-btn" onClick={handleDownloadFile}>
          <Download size={16} />
          <span>File</span>
        </button>
        <div className="sip-action-divider" />
        <button className="sip-action-btn refresh" onClick={fetchWorkOrder} title="Refresh">
          <RefreshCw size={16} />
        </button>
      </div>

      <div className={`sip-split-container ${showPdfViewer ? "with-pdf" : "no-pdf"} ${pdfFullscreen ? "pdf-fullscreen" : ""}`}>
        {showPdfViewer && (
          <div className="sip-pdf-panel">
            <div className="sip-pdf-header">
              <h3>{isPdf ? <FileText size={18} /> : <Image size={18} />}{workOrder.filename || "Document"}</h3>
              <div className="sip-pdf-controls">
                <span className="sip-file-type-badge">{workOrder.filename?.split(".").pop()?.toUpperCase() || "FILE"}</span>
                <button className="sip-pdf-control-btn" onClick={() => setPdfFullscreen(!pdfFullscreen)} title={pdfFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                  {pdfFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
                <button className="sip-pdf-control-btn" onClick={handleDownloadFile} title="Download"><Download size={16} /></button>
              </div>
            </div>
            <div className="sip-pdf-viewer">
              {fileLoading ? (
                <div className="sip-pdf-unavailable"><RefreshCw size={48} className="sip-spin" /><p>Loading document...</p></div>
              ) : fileUrl && !fileLoadError ? (
                isPdf ? <iframe src={fileUrl} className="sip-pdf-iframe" title="Document Viewer" /> : (
                  <div className="sip-image-viewer-scroll"><img src={fileUrl} alt="Document" className="sip-document-image" /></div>
                )
              ) : (
                <div className="sip-pdf-unavailable"><FileText size={48} /><p>Document not available</p><button className="sip-download-link" onClick={handleDownloadFile}><Download size={16} /> Download File</button></div>
              )}
            </div>
          </div>
        )}

        <div className="sip-data-panel">
          <div className="sip-tabs">
            <button className={`sip-tab ${activeTab === "extracted" ? "active" : ""}`} onClick={() => setActiveTab("extracted")}><FileText size={16} /> Table View</button>
            <button className={`sip-tab ${activeTab === "raw" ? "active" : ""}`} onClick={() => setActiveTab("raw")}><FileJson size={16} /> Raw JSON</button>
            <button className={`sip-tab ${activeTab === "signatures" ? "active" : ""}`} onClick={() => setActiveTab("signatures")}><Image size={16} /> Signatures</button>
            <button className={`sip-tab ${activeTab === "metadata" ? "active" : ""}`} onClick={() => setActiveTab("metadata")}><Hash size={16} /> Metadata</button>
          </div>

          <div className="sip-tab-content">
            {activeTab === "extracted" && (
              <div>
                {Object.keys(tableData).length === 0 ? (
                  <div className="sip-no-data"><FileText size={48} /><p>No extracted data available yet</p></div>
                ) : (
                  Object.entries(tableData).map(([section, fields]) => (
                    <CollapsibleSection key={section} title={section} defaultOpen={true}>
                      <div className="sip-fields-table">
                        <table>
                          <thead><tr><th>Field</th><th>Value</th><th></th></tr></thead>
                          <tbody>
                            {Object.entries(fields || {}).map(([field, value]) => {
                              const display = value === null || value === undefined ? "" : typeof value === "object" ? JSON.stringify(value) : String(value);
                              return (
                                <tr key={field}>
                                  <td className="sip-field-name">{field.replace(/_/g, " ")}</td>
                                  <td className="sip-field-value">{display || <em style={{ color: "#9ca3af" }}>—</em>}</td>
                                  <td className="sip-field-actions">
                                    {display && (
                                      <button className="sip-copy-btn" onClick={() => copyToClipboard(display, `${section}.${field}`)}>
                                        {copiedField === `${section}.${field}` ? <CheckCircle size={14} style={{ color: "#10b981" }} /> : <Copy size={14} />}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CollapsibleSection>
                  ))
                )}
              </div>
            )}

            {activeTab === "raw" && (
              <div className="sip-raw-json">
                <div className="sip-raw-json-header">
                  <button onClick={() => copyToClipboard(JSON.stringify(extractionResult || workOrder, null, 2), "raw_json")}>
                    <Copy size={14} />{copiedField === "raw_json" ? "Copied!" : "Copy"}
                  </button>
                  <button onClick={handleDownloadJson}><Download size={14} /> Download</button>
                </div>
                <div className="sip-json-viewer"><JsonRenderer data={extractionResult || workOrder} /></div>
              </div>
            )}

            {activeTab === "signatures" && (
              <div className="sip-signatures-section">
                <h3>Signature Images</h3>
                {Object.keys(signatures).length === 0 ? (
                  <div className="sip-no-signatures"><Image size={48} /><p>No signatures found</p><span className="sip-signatures-hint">Signatures are extracted from the registration form when available.</span></div>
                ) : (
                  <div className="sip-signatures-grid">
                    {Object.entries(signatures).map(([key, val]) => (
                      <div key={key} className="sip-signature-card">
                        <div className="sip-signature-header"><Image size={16} />{key.replace(/_/g, " ")}</div>
                        <div className="sip-signature-image-container">
                          <img src={val.startsWith("data:image") ? val : `data:image/png;base64,${val}`} alt={key} className="sip-signature-image" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "metadata" && (
              <div className="sip-metadata-section">
                <h3>Work Order Metadata</h3>
                <table className="sip-metadata-table">
                  <tbody>
                    {[
                      ["Execution ID", workOrder.execution_id],
                      ["Barcode", workOrder.barcode],
                      ["Fund Code", workOrder.fund],
                      ["Fund Name", workOrder.fund_name],
                      ["Transaction Type", workOrder.transaction_type || workOrder.trtype],
                      ["SIP Status", workOrder.sip_status],
                      ["Success", String(workOrder.success)],
                      ["Page Count", workOrder.page_count],
                      ["Time Taken", workOrder.time_taken_in_min ? `${workOrder.time_taken_in_min} min` : "-"],
                      ["Start Time", workOrder.start_time],
                      ["End Time", workOrder.end_time],
                      ["Created At", formatDate(workOrder.created_at)],
                      ["Filename", workOrder.filename || "-"],
                      ["File Size", workOrder.file_size ? `${(workOrder.file_size / 1024).toFixed(1)} KB` : "-"],
                      ["GridFS File ID", workOrder.gridfs_file_id || "N/A"],
                      ["MongoDB ID", workOrder._id],
                    ].map(([label, value]) => (
                      <tr key={label}><td>{label}</td><td>{value || "-"}</td></tr>
                    ))}
                    {workOrder.error && <tr className="sip-error-row"><td>Error</td><td style={{ color: "#dc2626" }}>{workOrder.error}</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SIPWorkOrderDetail;
